import React, { useEffect, useMemo, useState } from 'react';
import TripRouteAccordionContent from '@/components/modules/trip-route-details/trip-route-accordion/trip-route-accordion-content/trip-route-accordion-content';
import { InteractedItem } from '@/components/modules/trip-route-details/trip-route-details';
import {
  isDescriptionItem,
  isTransportModeItem,
} from '@/components/modules/trip-route-details/trip-route-details.adapter';
import TripRouteDetailsTransportMode from '@/components/modules/trip-route-details/trip-route-details-transport-mode/trip-route-details-transport-mode';

export interface TripRouteAccordionCombinedProps {
  /** The interacted item */
  interactedItem?: InteractedItem;
  /** Indicates whether the accordion is open */
  isActive?: boolean;
  /** The identification for the accordion */
  itemIndex?: number;
  /** An event that is triggered to pass the accordion state */
  onChange?: (value: boolean) => void;
  /** An event that is triggered on click */
  onClick?: () => void;
  /** An event that is triggered when accordion item is selected */
  onSelect?: (id?: string) => void;
  /** The items to combine in the accordion block */
  subItems: (
    | Content.Properties.DescriptionItem
    | Content.Properties.TransportModeItem
  )[];
}

interface AccordionSubItem {
  isActive: boolean;
  itemId?: string;
}

const TripRouteAccordionCombined = ({
  interactedItem,
  isActive,
  itemIndex,
  onChange,
  onClick,
  onSelect,
  subItems,
}: TripRouteAccordionCombinedProps) => {
  const initialAccordionSubItems = useMemo(
    () =>
      subItems.filter(isDescriptionItem).map(subItem => ({
        isActive: false,
        itemId: subItem.pointOfInterest?.id,
      })),
    [subItems],
  );

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [isExternallyTriggered, setIsExternallyTriggered] = useState(true);
  const [accordionSubItems, setAccordionSubItems] = useState<
    AccordionSubItem[]
  >(initialAccordionSubItems);

  // ! NOTE: If the accordion is closed as a result of another accordion being opened
  useEffect(() => {
    if (!isActive) {
      setIsExternallyTriggered(true);
      setAccordionSubItems(initialAccordionSubItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    setIsAccordionExpanded(accordionSubItems.some(item => item.isActive));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accordionSubItems]);

  // ! NOTE: Execute onClick to update isActive only if not externally triggered
  useEffect(() => {
    if (!isExternallyTriggered && onClick) {
      onClick();
    }

    if (onChange) {
      onChange(isAccordionExpanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccordionExpanded]);

  useEffect(() => {
    if (!interactedItem) return;

    const subItemExists = subItems.find(
      item =>
        isDescriptionItem(item) &&
        item.pointOfInterest?.id === interactedItem.itemId,
    );

    if (!subItemExists) return;

    const itemId = interactedItem.itemId;

    setIsExternallyTriggered(false);
    setAccordionSubItems(prev => {
      return prev.map(item =>
        itemId && item.itemId === itemId
          ? { ...item, isActive: !item.isActive }
          : { ...item, isActive: false },
      );
    });

    onSelect?.(itemId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactedItem]);

  const getAccordionItem = (
    item: Content.Properties.DescriptionItem,
    subItemIndex?: number,
  ) => {
    const itemId = item.pointOfInterest?.id;

    const handleClickItem = () => {
      setIsExternallyTriggered(false);
      setAccordionSubItems(prev => {
        return prev.map(item =>
          itemId && item.itemId === itemId
            ? { ...item, isActive: !item.isActive }
            : { ...item, isActive: false },
        );
      });

      if (onSelect) {
        onSelect(itemId);
      }
    };

    const getIsItemExpanded = () => {
      return accordionSubItems.find(item => item.itemId === itemId)?.isActive;
    };

    return (
      <TripRouteAccordionContent
        key={itemId}
        isActive={getIsItemExpanded()}
        item={item}
        itemIndex={itemIndex}
        onClick={handleClickItem}
        subItemIndex={subItemIndex}
      />
    );
  };

  const renderItems = () => {
    return subItems.map((subItem, subItemIndex) => {
      if (isDescriptionItem(subItem)) {
        return getAccordionItem(subItem, subItemIndex);
      }

      if (isTransportModeItem(subItem)) {
        return (
          <TripRouteDetailsTransportMode key={subItemIndex} item={subItem} />
        );
      }
    });
  };

  return <div>{renderItems()}</div>;
};

TripRouteAccordionCombined.displayName = 'TripRouteAccordionCombined';

export default TripRouteAccordionCombined;
